import type { fnGetFilteredProductLinks } from '@atlassian/global-shortcuts';
import type { ShortcutsConfig } from '@atlassian/global-side-navigation/types';

import {
	type Collaborator,
	INVITED_APP_SHORTCUTS_ALLOWED_APS_KEYS,
	INVITED_APP_SHORTCUTS_ALLOWED_TARGET_APPS,
	type InvitedAppShortcutAllowedApsKey,
	type InvitedAppShortcutAllowedTargetAppKey,
} from '../../../common/types';
import { getAppKeyByApsKey } from '../../../common/utils/invited-app-shortcuts-utils';
import type { CollaboratorsWithProducts } from '../../collaborators-in-products';
import {
	CACHE_WRITE_ID_KEY,
	type CachedAppState,
	type CachedState,
	type ExperimentDetails,
	FirstActiveSpecialValues,
	type FullContext,
	IneligibilityReason,
	type IneligibleExperimentDetails,
	type ProductInState,
	ProductsInState,
	type StoredTimestamp,
	type Trait,
	type Traits,
} from '../types';

const timespan = ({ days = 0, minutes = 0 }) => (days * 24 * 60 + minutes) * 60 * 1000;
const TIME_IN_SOURCE_PRODUCT_BEFORE_ELIGIBLE = timespan({ days: 3 });

/**
 * When the customer visits after this time since we last wrote data to the cache,
 * we'll refresh the cache: refetch the required data.
 */
export const CACHE_TIME_UNTIL_REFRESH = timespan({ days: 7 });

/**
 * When the customer visits after this time since we last wrote data to the cache,
 * we'll consider the cache expired and invalid, and not eligible to see the experiment
 * (the user will not see the treatment on that visit).
 * We will refresh the cache at that time, but even if that comes back as eligible,
 * it will only affect future visits.
 */
export const CACHE_TIME_UNTIL_EXPIRY = timespan({ days: 10 });

export type ResolvedCacheDetails = {
	eligibility: ExperimentDetails;
	state?: CachedState;
};

export const resolveFromCache = ({
	sourceProduct,
	targetProducts,
	cache,
}: Pick<FullContext, 'sourceProduct' | 'targetProducts' | 'cache'>): ResolvedCacheDetails => {
	if (targetProducts.length === 0) {
		return { eligibility: ineligible(IneligibilityReason.NO_TARGET_PRODUCTS) };
	}

	const cached = cache.read({ ssr: true });

	if (
		!cached ||
		Object.keys(cached).length === 0 ||
		(Object.keys(cached).length === 1 && CACHE_WRITE_ID_KEY in cached)
	) {
		return { eligibility: ineligible(IneligibilityReason.CACHE_EMPTY) };
	}

	const resolvedDetails = resolveExperimentDetails({ sourceProduct, targetProducts }, cached);

	return { eligibility: resolvedDetails, state: cached };
};

export const resolveExperimentDetails = (
	{
		sourceProduct,
		targetProducts,
		timestamp = Date.now(),
	}: Pick<FullContext, 'sourceProduct' | 'targetProducts'> & { timestamp?: number },
	cachedState: CachedState,
): ExperimentDetails => {
	if (targetProducts.length === 0) {
		return ineligible(IneligibilityReason.NO_TARGET_PRODUCTS);
	}
	if (
		!(
			cachedState.firstActiveUpdatedTimestamp &&
			timestamp <= cachedState.firstActiveUpdatedTimestamp + CACHE_TIME_UNTIL_EXPIRY
		)
	) {
		return ineligible(IneligibilityReason.CACHE_EXPIRED);
	}

	if (sourceProduct !== 'home') {
		const firstActiveInSourceProduct = cachedState.apps?.[sourceProduct]?.firstActive;
		if (!firstActiveInSourceProduct) {
			return ineligible(IneligibilityReason.CACHE_EMPTY);
		}
		if (timestamp - firstActiveInSourceProduct < TIME_IN_SOURCE_PRODUCT_BEFORE_ELIGIBLE) {
			return ineligible(IneligibilityReason.SOURCE_PRODUCT_TOO_NEW);
		}
	}

	let invitedApps: ExperimentDetails['invitedApps'] = {};
	let hasAccessToAtLeastOneTarget = false;
	let atLeastOneTargetInactive = false;

	for (const targetProduct of targetProducts) {
		const cachedApp = cachedState.apps?.[targetProduct];
		if (cachedApp?.access) {
			hasAccessToAtLeastOneTarget = true;

			if (cachedApp?.firstActive === FirstActiveSpecialValues.INACTIVE) {
				atLeastOneTargetInactive = true;

				invitedApps[targetProduct] = {
					eligible: true,
					collaborators: Array.from({ length: cachedApp.collaboratorCount ?? 0 }).map(
						(_, index) => cachedApp.collaboratorDetails?.[index] ?? null,
					),
					firstImpressionAt: cachedApp?.firstImpressionAt ?? null,
				};
			}
		}
	}
	if (!hasAccessToAtLeastOneTarget) {
		return ineligible(IneligibilityReason.NO_ACCESS_TO_TARGET_PRODUCTS);
	}
	if (!atLeastOneTargetInactive) {
		return ineligible(IneligibilityReason.TARGET_PRODUCTS_ALL_ACTIVE);
	}
	return {
		ineligibilityReason: null,
		invitedApps,
	};
};

export const ineligible = (reason: IneligibilityReason): IneligibleExperimentDetails => ({
	ineligibilityReason: reason,
});

export const isFirstActiveExpiringSoonNeedsRefresh = (
	cachedState: CachedState | undefined,
): boolean =>
	!cachedState?.firstActiveUpdatedTimestamp ||
	Date.now() > cachedState.firstActiveUpdatedTimestamp + CACHE_TIME_UNTIL_REFRESH;

export type AccessMap = {
	[target in ProductInState]?: boolean;
};

export const updateCachedEligibility = async ({
	access: accessMap,
	traits,
	cache,
}: {
	access: AccessMap;
	traits: Traits;
} & Pick<FullContext, 'cache'>): Promise<CachedState> => {
	const prev = cache.read({ ssr: false });
	const draft = { ...prev, apps: { ...prev?.apps } };
	const traitsByName = groupTraitsByName(traits);
	for (const product of ProductsInState) {
		const productAccess = accessMap[product];
		const traitValue = getProductFirstActiveTraitValue(product, traitsByName);
		if (traitValue !== FIRST_ACTIVE_NOT_APPLICABLE) {
			const firstActive = traitValue ? toStoredTimestamp(traitValue) : null;
			const draftApp: CachedAppState = {
				...draft.apps[product],
			};
			if (firstActive) {
				draftApp.firstActive = firstActive;
			} else if (productAccess === true) {
				draftApp.firstActive = FirstActiveSpecialValues.INACTIVE;
			}
			if (productAccess !== undefined) {
				draftApp.access = productAccess;
			}
			draft.apps[product] = draftApp;
		}
	}
	draft.firstActiveUpdatedTimestamp = Date.now();

	cache.write(draft);

	return draft;
};

const FIRST_ACTIVE_NOT_APPLICABLE = Symbol('FIRST_ACTIVE_NOT_APPLICABLE');

const getProductFirstActiveTraitValue = (
	product: ProductInState,
	traitsByName: TraitsByName,
): Trait['value'] | typeof FIRST_ACTIVE_NOT_APPLICABLE | undefined => {
	switch (product) {
		case 'jira':
			return traitsByName['jira_first_active'] || traitsByName['jira-software_first_active'];
		case 'confluence':
			return traitsByName['confluence_first_active'];
		case 'home':
			return FIRST_ACTIVE_NOT_APPLICABLE;
		default:
			product satisfies never; // Should never get here - handle all products above!
	}
};

export const hasAccessToAnyTargetProduct = (
	access: AccessMap,
	targetProducts: ReadonlyArray<InvitedAppShortcutAllowedTargetAppKey>,
): boolean => targetProducts.some((product) => access[product] === true);

export type GetAppAccessInput = {
	getFilteredProductLinks: fnGetFilteredProductLinks;
	limitToOnlyApsKeys?: string[] | null;
};
export const getAppAccessFromProductLinksFilter = ({
	getFilteredProductLinks,
	limitToOnlyApsKeys = null,
}: GetAppAccessInput) => {
	const accessMap: AccessMap = {};
	for (const app of INVITED_APP_SHORTCUTS_ALLOWED_TARGET_APPS) {
		const apsKey = TargetToApsKey[app];
		if (!limitToOnlyApsKeys || limitToOnlyApsKeys.includes(apsKey)) {
			const { promotedLink, links, status } = getFilteredProductLinks(apsKey);
			if (status === 'complete') {
				const hasAccess = Boolean(promotedLink || links.length > 0);
				accessMap[app] = hasAccess;
			}
		}
	}
	return accessMap;
};

export type GetAppAccessByShortcutsInput = {
	limitToOnlyApsKeys?: InvitedAppShortcutAllowedApsKey[];
	shortcuts: ShortcutsConfig;
};

/**
 * Get the app access by the shortcuts
 * @param shortcuts - The shortcuts to get the app access for
 * @param limitToOnlyApsKeys - The APS keys to limit the access to
 * @returns The app access map
 */
export const getAppAccessFromShortcuts = ({
	shortcuts,
	limitToOnlyApsKeys = [...INVITED_APP_SHORTCUTS_ALLOWED_APS_KEYS],
}: GetAppAccessByShortcutsInput): AccessMap => {
	const accessMap: AccessMap = Object.values(shortcuts).reduce<AccessMap>((acc, shortcut) => {
		if (shortcut.type === 'link' && shortcut.apsKey) {
			const { apsKey, url } = shortcut;
			// if the url is valid then we assume the user has access to the product
			if (url && limitToOnlyApsKeys.includes(apsKey as InvitedAppShortcutAllowedApsKey)) {
				try {
					const appKey = getAppKeyByApsKey(apsKey as InvitedAppShortcutAllowedApsKey);
					acc[appKey] = true;
					// eslint-disable-next-line no-unused-vars
				} catch (_) {
					// if fails to get the app key then it's not an allowed app, skip it
				}
			}
		}

		return acc;
	}, {});
	return accessMap;
};

const TargetToApsKey: { [key in InvitedAppShortcutAllowedTargetAppKey]: string } = {
	jira: 'JIRA_SOFTWARE',
	confluence: 'CONFLUENCE',
};

export const updateFirstImpression = async ({
	productsSeen,
	seenTimestamp = Date.now(),
	cache,
}: Pick<FullContext, 'cache'> & {
	productsSeen: InvitedAppShortcutAllowedTargetAppKey[];
	seenTimestamp?: number;
}): Promise<void> => {
	const prev = cache.read({ ssr: false });

	const draft = { ...prev, apps: { ...prev?.apps } };
	let updated = false;
	for (const product of productsSeen) {
		const prevApp = draft.apps[product];
		if (!prevApp?.firstImpressionAt) {
			updated = true;
			draft.apps[product] = {
				...prevApp,
				firstImpressionAt: seenTimestamp,
			};
		}
	}

	if (updated) {
		await cache.write(draft);
	}
};

export const updateActivation = async ({
	cache,
	product,
	timestamp = Date.now(),
}: Pick<FullContext, 'cache'> & {
	product: InvitedAppShortcutAllowedTargetAppKey;
	timestamp?: number;
}): Promise<void> => {
	const prev = cache.read({ ssr: false });

	const draft = { ...prev, apps: { ...prev?.apps } };
	draft.apps[product] = {
		...draft.apps[product],
		firstActive: timestamp,
	};

	await cache.write(draft);
};

export const updateCollaborators = async ({
	products,
	collaboratorsWithProducts,
	cache,
}: Pick<FullContext, 'cache'> & {
	collaboratorsWithProducts: CollaboratorsWithProducts;
	products: ReadonlyArray<InvitedAppShortcutAllowedTargetAppKey>;
}) => {
	const prev = cache.read({ ssr: false });
	const collaboratorsByProduct = groupCollaboratorsByProduct(collaboratorsWithProducts);

	const draft = { ...prev, apps: { ...prev?.apps } };
	for (const product of products) {
		const collabs = collaboratorsByProduct[product] ?? [];
		draft.apps[product] = {
			...draft.apps[product],

			// We display:
			// - 1x avatar for 1 collaborator
			// - 2x avatars for exactly 2 collaborators
			// - 1x avatar + number of remaining collaborators for 3+
			// So store exactly as much detailed data as we need to display:
			collaboratorDetails: collabs
				.slice(0, collabs.length === 2 ? 2 : 1)
				.map((c) => ({ avatar: c.avatar })),
			collaboratorCount: collabs.length,
		};
	}

	await cache.write(draft);
};

type CollaboratorsByProduct = { [key in InvitedAppShortcutAllowedTargetAppKey]?: Collaborator[] };
const groupCollaboratorsByProduct = (
	collaboratorsWithProducts: CollaboratorsWithProducts,
): CollaboratorsByProduct => {
	const result: CollaboratorsByProduct = {};
	for (const cwp of collaboratorsWithProducts) {
		for (const product of cwp.products) {
			if (!result[product]) {
				result[product] = [];
			}
			result[product].push(cwp.collaborator);
		}
	}
	return result;
};

type TraitsByName = Record<Trait['name'], Trait['value']>;
const groupTraitsByName = (traits: Traits) => {
	const traitsByName: TraitsByName = {};
	for (const trait of traits) {
		traitsByName[trait.name] = trait.value;
	}
	return traitsByName;
};

export const toStoredTimestamp = (traitValue: Traits[number]['value']): StoredTimestamp | null =>
	(typeof traitValue === 'string' && +new Date(traitValue)) || null;
